import React from 'react';
import { Icon } from '@iconify/react';

export interface SegmentedOption {
  /** Stable key used as the value. */
  value: string;
  /** Optional iconify icon name (e.g. 'bx:inbox'). */
  icon?: string;
  /** Visible label. */
  label?: string;
  /** Optional numeric badge rendered after the label. */
  badge?: number | string;
}

export interface SegmentedControlProps {
  options: SegmentedOption[];
  value: string;
  onChange: (value: string) => void;
  /** Visual size. */
  size?: 'sm' | 'md' | 'lg';
  /** Tint of the active segment. */
  tone?: 'neutral' | 'accent';
  /** Aria label for the whole control. */
  ariaLabel?: string;
  className?: string;
}

/**
 * SegmentedControl — a reusable toggle between a small set of mutually
 * exclusive options (view switchers, inbox/queue toggles, etc.).
 *
 * Stateless and fully controlled by `value`/`onChange`. Keeps a consistent
 * compact pill aesthetic that matches the project's dark UI.
 */
export const SegmentedControl: React.FC<SegmentedControlProps> = ({
  options,
  value,
  onChange,
  size = 'md',
  tone = 'neutral',
  ariaLabel,
  className = '',
}) => {
  const padY = size === 'sm' ? 'py-1.5' : size === 'lg' ? 'py-2.5' : 'py-2';
  const textSize = size === 'sm' ? 'text-[10px]' : size === 'lg' ? 'text-xs' : 'text-[11px]';
  const iconSize = size === 'sm' ? 'h-3 w-3' : size === 'lg' ? 'h-4 w-4' : 'h-3.5 w-3.5';
  const padX = size === 'lg' ? 'px-4' : 'px-3';

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={`inline-flex items-center gap-1 rounded-[18px] border border-white/[0.045] bg-white/[0.018] p-1 shadow-[inset_0_1px_0_rgba(255,255,255,0.018)] ${className}`}
    >
      {options.map((option) => {
        const active = option.value === value;
        const activeBg = active
          ? tone === 'accent'
            ? 'border-cyan-300/35 bg-cyan-400/[0.12] text-cyan-100 shadow-[0_0_24px_rgba(34,211,238,0.14)]'
            : 'border-transparent bg-white/[0.07] text-white'
          : 'border-transparent text-white/40 hover:bg-white/[0.045] hover:text-white/75';

        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(option.value)}
            className={`inline-flex items-center gap-1.5 rounded-xl border ${padX} ${padY} ${textSize} font-black uppercase tracking-[0.12em] transition-all ${activeBg}`}
          >
            {option.icon && <Icon icon={option.icon} className={iconSize} />}
            {option.label && <span className="hidden min-[440px]:inline">{option.label}</span>}
            {option.badge !== undefined && (
              <span className="rounded-full bg-white/[0.08] px-1.5 py-0.5 text-[9px] font-bold text-white/55">
                {option.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default SegmentedControl;
